import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import { AuthProvider } from './AuthContext';
import { I18nProvider } from './I18nContext';
import { ToastProvider } from './ToastContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import OfflineBanner from './components/OfflineBanner';
import HomePage from './pages/HomePage';
import { TreePine, Home, ArrowLeft } from 'lucide-react';

const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const FarmersPage = lazy(() => import('./pages/FarmersPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const MyTreesPage = lazy(() => import('./pages/MyTreesPage'));
const TreeFormPage = lazy(() => import('./pages/TreeFormPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const NDVISummaryPage = lazy(() => import('./pages/NDVISummaryPage'));
const EUDRDashboardPage = lazy(() => import('./pages/EUDRDashboardPage'));
const QRCodeLabelsPage = lazy(() => import('./pages/QRCodeLabelsPage'));
const CRCFPage = lazy(() => import('./pages/CRCFPage'));
const FieldValidationPage = lazy(() => import('./pages/FieldValidationPage'));
const TreePublicPage = lazy(() => import('./pages/TreePublicPage'));
const MRVDashboardPage = lazy(() => import('./pages/MRVDashboardPage'));

function NotFoundPage() {
  return (
    <div style={{
      minHeight: 'calc(100vh - 56px)', background: '#fafaf9',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '40px 24px', fontFamily: "'Inter', sans-serif",
    }}>
      <div style={{ textAlign: 'center', maxWidth: 440 }}>
        <div style={{
          width: 80, height: 80, borderRadius: 22, background: '#f0f7f0',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 24px',
        }}>
          <TreePine size={40} style={{ color: '#2e7d32' }} />
        </div>
        <div style={{ fontSize: '5rem', fontWeight: 800, color: '#e8f5e9', lineHeight: 1, marginBottom: 8, letterSpacing: '-4px' }}>404</div>
        <h1 style={{ fontSize: '1.6rem', fontWeight: 800, color: '#1a2e1a', margin: '0 0 12px', letterSpacing: '-0.5px' }}>
          Page not found
        </h1>
        <p style={{ color: '#5a7a5a', fontSize: '0.95rem', lineHeight: 1.65, margin: '0 0 32px' }}>
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to="/" style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            background: '#1b5e20', color: '#fff',
            padding: '13px 24px', borderRadius: 12,
            fontWeight: 700, fontSize: '0.9rem', textDecoration: 'none',
            boxShadow: '0 2px 8px rgba(27,94,32,0.25)',
          }}>
            <Home size={16} /> Go Home
          </Link>
          <button onClick={() => window.history.back()} style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            background: '#fff', color: '#2e7d32',
            padding: '13px 24px', borderRadius: 12,
            fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer',
            border: '1.5px solid #c8e6c9',
          }}>
            <ArrowLeft size={16} /> Go Back
          </button>
        </div>
      </div>
    </div>
  );
}

function Loader() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <div style={{
        width: 32, height: 32, border: '3px solid #e8f5e9', borderTop: '3px solid #2e7d32',
        borderRadius: '50%', animation: 'spin 0.8s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <I18nProvider>
        <ToastProvider>
        <ErrorBoundary>
          <Layout>
            <OfflineBanner />
            <Suspense fallback={<Loader />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/farmers" element={<FarmersPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/my-trees" element={<ProtectedRoute><MyTreesPage /></ProtectedRoute>} />
                <Route path="/trees/add" element={<ProtectedRoute><TreeFormPage /></ProtectedRoute>} />
                <Route path="/trees/edit/:id" element={<ProtectedRoute><TreeFormPage /></ProtectedRoute>} />
                <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
                <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
                <Route path="/ndvi" element={<ProtectedRoute><NDVISummaryPage /></ProtectedRoute>} />
                <Route path="/eudr-compliance" element={<ProtectedRoute><EUDRDashboardPage /></ProtectedRoute>} />
                <Route path="/tree-labels" element={<ProtectedRoute><QRCodeLabelsPage /></ProtectedRoute>} />
                <Route path="/carbon-farming" element={<ProtectedRoute><CRCFPage /></ProtectedRoute>} />
                <Route path="/field-validation" element={<ProtectedRoute><FieldValidationPage /></ProtectedRoute>} />
                <Route path="/mrv" element={<ProtectedRoute><MRVDashboardPage /></ProtectedRoute>} />
                <Route path="/tree/:id" element={<TreePublicPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </Layout>
        </ErrorBoundary>
        </ToastProvider>
        </I18nProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
